import type { LocationQuery } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';

import { useRouterStore } from '@apps/main/new-desktop/core/stores';
import {
  TripPlannerPage,
  mapPathToTripPlannerPage,
} from '@apps/main/new-desktop/core/models/navigation';

import { isMetaHotelUser } from '@libs/cookie';
import { Landing } from '@libs/trip-planner-interfaces';
import {
  exhaustiveGuard,
  isDefined,
  performanceMark,
  PerformancePoint,
} from '@libs/utils';

import { TripPlannerRoute } from './tripPlannerRoute';

const router = createRouter({
  history: createWebHistory(),
  routes: [TripPlannerRoute.config],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    /* NOTE: Handle hash-based navigation (e.g. #availability).
     * Vue Router will try to scroll to the element immediately after route change.
     * ⚠️ If the target element belongs to a child component that loads asynchronously
     * (like HotelDetailsPanel sections), it may not be in the DOM yet,
     * so the scroll can fail with a warning.
     * In that case, the fallback logic inside HotelDetailsPanel (onContentRendered)
     * will retry the scroll once the async content is mounted.
     */
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }

    const isSameParent = to.matched[1]?.name === from.matched[1]?.name;

    return isSameParent ? false : { top: 0 };
  },
});

router.beforeEach((to, from) => {
  const previousPage = mapPathToTripPlannerPage(from.path);
  const nextPage = mapPathToTripPlannerPage(to.path);

  if (previousPage !== nextPage) {
    const routerStore = useRouterStore();
    routerStore.previousTripPlannerPage = previousPage;

    if (isDefined(nextPage)) {
      trackPageNavigationInitiatedPerformance(nextPage);
    }
  }

  // NOTE: This part redirects
  // * form: /travel/plan?pax=rpp
  // * to:   /travel/plan/overview?pax=rpp
  // which is required for meta redirection
  if (nextPage === TripPlannerPage.BaseURL) {
    return hasIngredientQueryParams(to.query)
      ? {
          name: TripPlannerPage.TripOverview,
          query: to.query,
        }
      : {
          name: TripPlannerPage.BuildYourTrip,
          query: to.query,
        };
  }

  // NOTE: This part redirects
  // * form: /travel/plan/overview?pax=rpp
  // * to:   /travel/plan/build-your-trip?pax=rpp
  if (
    nextPage === TripPlannerPage.TripOverview &&
    !hasIngredientQueryParams(to.query)
  ) {
    return { name: TripPlannerPage.BuildYourTrip, query: to.query };
  }

  // NOTE: This part redirects meta hotel-only users away from
  // TripOverview straight to HotelSelection. The meta flow is detected via
  // landing=room, which is set by the meta link.
  if (
    previousPage === undefined &&
    nextPage === TripPlannerPage.TripOverview &&
    to.query.landing === Landing.room &&
    isMetaHotelUser() &&
    hasIngredientQueryParams(to.query)
  ) {
    return {
      name: TripPlannerPage.HotelSelection,
      params: { ingredientIndex: 'h0' },
      query: to.query,
    };
  }
});

function hasIngredientQueryParams(query: LocationQuery): boolean {
  return (
    !!query.recipe ||
    Object.keys(query).some((queryKey) => queryKey.match(/^i\d*$/))
  );
}

function trackPageNavigationInitiatedPerformance(
  destinationPage: TripPlannerPage
): void {
  switch (destinationPage) {
    case TripPlannerPage.BuildYourTrip:
      performanceMark(PerformancePoint.BuildYourTripPageNavigationInitiated);
      break;

    case TripPlannerPage.TripOverview:
      performanceMark(PerformancePoint.TripOverviewPageNavigationInitiated);
      break;

    case TripPlannerPage.HotelSelection:
      performanceMark(PerformancePoint.HotelSelectionPageNavigationInitiated);
      break;

    case TripPlannerPage.HotelDetailsInHotelSelection:
      performanceMark(PerformancePoint.HotelDetailsPageNavigationInitiated);
      break;

    case TripPlannerPage.HotelDetailsInTripOverview:
      performanceMark(PerformancePoint.HotelDetailsPageNavigationInitiated);
      break;

    case TripPlannerPage.ActivitySelection:
      performanceMark(
        PerformancePoint.ActivitySelectionPageNavigationInitiated
      );
      break;

    case TripPlannerPage.ActivityDetailsInActivitySelection:
      performanceMark(PerformancePoint.ActivityDetailsPageNavigationInitiated);
      break;

    case TripPlannerPage.ActivityDetailsInTripOverview:
      performanceMark(PerformancePoint.ActivityDetailsPageNavigationInitiated);
      break;

    case TripPlannerPage.FlightList:
      performanceMark(PerformancePoint.FlightSelectionPageNavigationInitiated);
      break;

    case TripPlannerPage.FerryList:
      performanceMark(PerformancePoint.FerrySelectionPageNavigationInitiated);
      break;

    case TripPlannerPage.BaseURL:
    case TripPlannerPage.TripOverviewPrint:
    case TripPlannerPage.HotelDetails:
    case TripPlannerPage.ActivityDetails:
    case TripPlannerPage.CarTrip:
      break;

    default:
      exhaustiveGuard(destinationPage, 'Unhandled trip planner page');
  }
}

export default router;
